import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[background-color,box-shadow,transform,border-color,color] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 active:translate-y-px',
  {
    variants: {
      variant: {
        primary:
          'bg-brand text-brand-foreground shadow-[0_1px_0_0_rgba(255,255,255,0.18)_inset,0_1px_2px_0_rgba(19,20,58,0.18)] hover:bg-brand-hover hover:shadow-[0_1px_0_0_rgba(255,255,255,0.2)_inset,0_4px_12px_-4px_rgba(49,51,128,0.45)] active:bg-brand-active',
        outline:
          'border border-line bg-surface text-ink hover:border-line-strong hover:bg-surface-tinted',
        ghost: 'text-ink-muted hover:bg-surface-muted hover:text-ink',
        danger:
          'bg-danger text-white shadow-[0_1px_0_0_rgba(255,255,255,0.18)_inset,0_1px_2px_0_rgba(0,0,0,0.18)] hover:bg-danger-hover',
        link: 'text-brand underline-offset-4 hover:underline',
        subtle: 'bg-surface-muted text-ink hover:bg-line',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4',
        lg: 'h-11 px-5 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />;
});

export { buttonVariants };
